import React, { useEffect, useState } from 'react';
import {
  Box,
  SimpleGrid,
  useColorModeValue,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import { isSoloLectura } from 'utils/permissions';
import MiniStatistics from 'components/card/MiniStatistics';
import IconBox from 'components/icons/IconBox';
import {
  MdSecurity,
  MdPerson,
  MdComputer,
  MdStorage,
} from 'react-icons/md';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  BarChart,
  Bar,
} from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];
const meses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const contarActivosPorMes = (activosAnidados: any[][] = []) => {
  const activosPlanos = activosAnidados.flat();
  const contador = Array(12).fill(0);
  activosPlanos.forEach((activo: any) => {
    if (activo.CreatedDate) {
      const fecha = new Date(activo.CreatedDate);
      const mes = fecha.getMonth();
      contador[mes]++;
    }
  });
  return meses.map((mes, index) => ({
    mes,
    activos: contador[index]
  }));
};

const contarResguardosPorMes = (resguardosAnidados: any[][] = []) => {
  const resguardosPlanos = resguardosAnidados.flat();
  const contador = Array(12).fill(0);
  resguardosPlanos.forEach((resguardo: any) => {
    if (resguardo.CreatedDate) {
      const fecha = new Date(resguardo.CreatedDate);
      const mes = fecha.getMonth();
      contador[mes]++;
    }
  });
  return meses.map((mes, index) => ({
    mes,
    resguardos: contador[index]
  }));
};

const contarActivosPorTipo = (activosPorTipo: any[][] = []) => {
  const conteoPorTipo: { [key: string]: number } = {};
  activosPorTipo[0].forEach((activo) => {
    const tipo = activo.Name;
    if (tipo) {
      conteoPorTipo[tipo] = (conteoPorTipo[tipo] || 0) + 1;
    }
  });
  const result = [];
  let otros = 0;
  Object.entries(conteoPorTipo).forEach(([name, value]) => {
    if (value >= 1) {
      result.push({ name, value });
    } else {
      otros += value;
    }
  });
  if (otros > 0) {
    result.push({ name: "Otros", value: otros });
  }
  return result;
};

const contarActivosPorEstado = (activosPorEstado: any[][] = []) => {
  const conteoPorEstado: { [key: string]: number } = {};
  const activosPlanos = activosPorEstado.flat();
  activosPlanos.forEach((activo) => {
    if (activo && activo.Name) {
      const estado = activo.Name;
      conteoPorEstado[estado] = (conteoPorEstado[estado] || 0) + 1;
    }
  });
  const estadoLabels: { [key: string]: string } = {
    'active': 'Activo',
    'assigned': 'Asignado',
    'pending': 'Pendiente',
    'inactive': 'Inactivo',
    'in_maintenance': 'En Mantenimiento',
    'in_repair': 'En Reparación',
    'under_warranty': 'Bajo Garantía',
    'rma': 'RMA',
  };
  const result = [];
  let otros = 0;
  Object.entries(conteoPorEstado).forEach(([name, value]) => {
    if (value >= 1) {
      result.push({ name: estadoLabels[name] || name, value });
    } else {
      otros += value;
    }
  });
  if (otros > 0) {
    result.push({ name: "Otros", value: otros });
  }
  return result;
};

export default function DashboardActivos() {
  const brandColor = useColorModeValue('brand.500', 'white');
  const boxBg = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');
  const textColor = useColorModeValue('secondaryGray.900', 'white');

  const [dashboardData, setDashboardData] = useState({
    cantidadActivos: 0,
    cantidadResguardos: 0,
    equiposEnMantenimiento: 0,
    usuariosActivos: 0,
    activosPorTipo: [],
    activosPorMes: [],
    resguardosPorMes: [],
    estadoActivos: []
  });

  const [nuevoActivo, setNuevoActivo] = useState({
    nombre: '',
    descripcion: '',
    tipo: '',
    ubicacion: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNuevoActivo((prev) => ({ ...prev, [name]: value }));
  };
  

  const handleGuardarActivo = async () => {
    try {
      const response = await fetch('https://inventariosbackend.velasresorts.com/inventarios/activos/crear', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(nuevoActivo),
      });

      if (!response.ok) throw new Error('Error al guardar el activo');

      const result = await response.json();
      alert('Activo creado correctamente');
      setNuevoActivo({ nombre: '', descripcion: '', tipo: '', ubicacion: '' });

    } catch (error) {
      console.error('Error al guardar:', error);
      alert('Ocurrió un error al guardar el activo');
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const activosResponse = await fetch('https://inventariosbackend.velasresorts.com/inventarios/activos/listado');
        const activosData = await activosResponse.json();
        const activosPorMes = contarActivosPorMes(activosData);

        const resguardosResponse = await fetch('https://inventariosbackend.velasresorts.com/inventarios/resguardos/listado/encabezado');
        const resguardosData = await resguardosResponse.json();
        const resguardosPorMes = contarResguardosPorMes(resguardosData);

        const activosPorTipoResponse = await fetch('https://inventariosbackend.velasresorts.com/inventarios/activos/listado/tipos');
        const activosDataPorTipo = await activosPorTipoResponse.json();
        const activosPorTipo = contarActivosPorTipo(activosDataPorTipo);

        const estadoActivosResponse = await fetch('https://inventariosbackend.velasresorts.com/inventarios/activos/listado/dashboard/estados');
        const estadoActivosData = await estadoActivosResponse.json();
        const estadoActivos = contarActivosPorEstado(estadoActivosData);

        const equiposEnMantenimiento = estadoActivos.find(item => item.name === 'En Mantenimiento')?.value || 0;

        setDashboardData(prev => ({
          ...prev,
          cantidadActivos: activosData[0].flat().length,
          cantidadResguardos: resguardosData[0].flat().length,
          equiposEnMantenimiento,
          activosPorMes,
          resguardosPorMes,
          activosPorTipo,
          estadoActivos
        }));
      } catch (err) {
        console.error('Error al cargar datos:', err);
      }
    };

    fetchData();
  }, []);

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      {/* Formulario para agregar activo */}
      {!isSoloLectura() && (
        <Card p="20px" mb="30px">
          <Box mb="20px" fontWeight="bold" color={textColor} fontSize="xl">
            Agregar nuevo activo
          </Box>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing="20px">
            <FormControl>
              <FormLabel>Nombre</FormLabel>
              <Input name="nombre" value={nuevoActivo.nombre} onChange={handleInputChange} />
            </FormControl>

            <FormControl>
              <FormLabel>Descripción</FormLabel>
              <Textarea name="descripcion" value={nuevoActivo.descripcion} onChange={handleInputChange} />
            </FormControl>

            <FormControl>
              <FormLabel>Tipo</FormLabel>
              <Input name="tipo" value={nuevoActivo.tipo} onChange={handleInputChange} />
            </FormControl>

            <FormControl>
              <FormLabel>Ubicación</FormLabel>
              <Input name="ubicacion" value={nuevoActivo.ubicacion} onChange={handleInputChange} />
            </FormControl>
          </SimpleGrid>

          <Button mt="6" colorScheme="teal" onClick={handleGuardarActivo}>
            Guardar Activo
          </Button>
        </Card>
      )}

      {/* Aquí seguiría tu dashboard con los gráficos y estadísticas */}
      {/* ... */}
    </Box>
  );
}
